import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/ui/accordion";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import { Check, Database, Layers } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useLanguage } from "@/core/LanguageContext";

export interface DatapointSelection {
  id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  datapoint: {
    display_name: string;
    field_name: string;
    measurement_name: string;
    bucket_name: string;
    unit?: { symbol: string } | null;
  } | null;
}

interface DatapointSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scopedMappings: DatapointSelection[];
  allMappings: DatapointSelection[];
  onSelect: (mapping: DatapointSelection) => void;
  title?: string;
  description?: string;
  selectedId?: string | null;
}

export const DatapointSelectorDialog = ({
  open,
  onOpenChange,
  scopedMappings,
  allMappings,
  onSelect,
  title,
  description,
  selectedId = null,
}: DatapointSelectorDialogProps) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");

  const scopedItems = useMemo(() => {
    return scopedMappings.filter((mapping) => mapping.datapoint);
  }, [scopedMappings]);

  const otherItems = useMemo(() => {
    const scopedIds = new Set(scopedMappings.map((mapping) => mapping.id));
    return allMappings.filter((mapping) => mapping.datapoint && !scopedIds.has(mapping.id));
  }, [allMappings, scopedMappings]);

  const filteredOtherItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return otherItems;

    return otherItems.filter((mapping) => {
      const datapoint = mapping.datapoint;
      if (!datapoint) return false;
      const haystack = [
        datapoint.display_name,
        datapoint.bucket_name,
        datapoint.measurement_name,
        datapoint.field_name,
        datapoint.unit?.symbol ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [otherItems, search]);

  const renderMapping = (mapping: DatapointSelection) => {
    const datapoint = mapping.datapoint;
    if (!datapoint) return null;
    return (
      <CommandItem
        key={mapping.id}
        value={`${datapoint.display_name} ${datapoint.measurement_name} ${datapoint.field_name}`}
        onSelect={() => {
          onSelect(mapping);
          onOpenChange(false);
          setSearch("");
        }}
        className="flex items-start gap-3"
      >
        <Check
          className={cn(
            "mt-1 h-4 w-4 text-primary",
            selectedId === mapping.id ? "opacity-100" : "opacity-0"
          )}
        />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{datapoint.display_name}</p>
            {datapoint.unit?.symbol && (
              <Badge variant="outline" className="text-xs">
                {datapoint.unit.symbol}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary" className="text-[10px]">
              {datapoint.bucket_name}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {datapoint.measurement_name}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {datapoint.field_name}
            </Badge>
          </div>
        </div>
      </CommandItem>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            {title || t("core.datapointSelector.title", "Select a datapoint")}
          </DialogTitle>
          <DialogDescription>
            {description || t("core.datapointSelector.description", "Choose an existing datapoint mapping.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm font-medium">
                {t("core.datapointSelector.scopedHeading", "Datapoints on this level")}
              </p>
              <Badge variant="secondary">{scopedItems.length}</Badge>
            </div>
            {scopedItems.length === 0 ? (
              <div className="px-4 pb-4 text-sm text-muted-foreground">
                {t("core.datapointSelector.scopedEmpty", "No datapoints found on this level.")}
              </div>
            ) : (
              <Command className="rounded-none border-t">
                <ScrollArea className="h-[240px]">
                  <CommandList className="max-h-none">
                    <CommandGroup>
                      {scopedItems.map((mapping) => renderMapping(mapping))}
                    </CommandGroup>
                  </CommandList>
                </ScrollArea>
              </Command>
            )}
          </div>

          <Accordion type="single" collapsible className="rounded-lg border">
            <AccordionItem value="all" className="border-none">
              <AccordionTrigger className="px-4">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {t("core.datapointSelector.otherHeading", "Browse all datapoints")}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <Command className="rounded-lg border">
                  <CommandInput
                    placeholder={t("core.datapointSelector.search", "Search all datapoints...")}
                    value={search}
                    onValueChange={setSearch}
                  />
                  <ScrollArea className="h-[240px]">
                    <CommandList className="max-h-none">
                      <CommandEmpty>
                        {t("core.datapointSelector.empty", "No datapoints match this search.")}
                      </CommandEmpty>
                      <CommandGroup
                        heading={`${t("core.datapointSelector.otherResults", "Other datapoints")}: ${filteredOtherItems.length}`}
                      >
                        {filteredOtherItems.map((mapping) => renderMapping(mapping))}
                      </CommandGroup>
                    </CommandList>
                  </ScrollArea>
                </Command>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DatapointSelectorDialog;
